import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';
import { ClozeTestComponent } from './components/cloze-test/cloze-test.component';
import { ClozeTestModel } from './components/model/cloze-test-model';
import { ClozeTestStyleModel } from './components/model/cloze-test-style-model';
import { IntroductionType } from './components/model/introduction-type.enum';
import { UrlService } from '../../shared/services/common/url.service';
import { PlacementTestsService } from './components/services/placement-tests.service';

@Component({
  selector: 'app-study-guide-placement-test',
  templateUrl: './study-guide-placement-test.component.html',
  styleUrls: ['./study-guide-placement-test.component.scss'],
  standalone: true,
  imports: [CommonModule, ClozeTestComponent],
})
export class StudyGuidePlacementTestComponent implements OnInit, OnDestroy {

  readonly #route = inject(ActivatedRoute);
  readonly #deviceService = inject(DeviceDetectorService);
  readonly #title = inject(Title);
  readonly #document = inject(DOCUMENT);
  readonly #urlService = inject(UrlService);
  readonly #placementTestsService = inject(PlacementTestsService);

  onsetBookingId = signal<number | null>(null);
  onsetStarted = signal<boolean | null>(false);
  mobileMode = signal(false);
  lang = signal('de');

  subscription = new Subscription();

  cTestLogo = signal('');
  clozeTestModel = signal<ClozeTestModel>({} as ClozeTestModel);
  clozeTestStyleModel = signal<ClozeTestStyleModel>({} as ClozeTestStyleModel);
  cTestButtonColor = signal('');
  introductionType = signal(IntroductionType.INTRO_SHORT);

  #prevTitle = '';
  #prevFavicon = '';

  ngOnInit() {
    this.#route.params.subscribe(params => {
      if (params['onsetBookingId']) {
        this.onsetBookingId.set(+params['onsetBookingId']);
      }
    });

    this.#route.queryParams.subscribe(params => {
      if (params['lang']) {
        this.lang.set(params['lang']);
      }
    });

    this.#prevTitle = this.#title.getTitle();
    this.#title.setTitle('Study Guide - Spracheinstufung');

    const favicon = this.#document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) {
      this.#prevFavicon = favicon.href;
      favicon.type = 'image/svg+xml';
      favicon.href = new URL('assets/daad-logo.svg', this.#document.baseURI).href;
    }

    // detect mobile
    this.mobileMode.set(this.#deviceService.isMobile());

    this.clozeTestStyleModel.set(this.#generateClozeTestStyle());
    this.clozeTestModel.set(this.#generateClozeTestModel());
  }

  ngOnDestroy() {
    this.#title.setTitle(this.#prevTitle);

    const favicon = this.#document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon && this.#prevFavicon) {
      favicon.type = 'image/x-icon';
      favicon.href = this.#prevFavicon;
    }
  }

  onStartTest(_start: boolean): void {
    console.log(`test started`);
  }

  onBookingInit(gastBookingId: number): void {
    console.log(`init gastBookingId ${gastBookingId}`);
  }

  onTestHasBeenSaved(gastBookingId: number): void {
    console.log(`saving gastBookingId ${gastBookingId}`);
  }

  onClozeTestCompleted(): void {
    const bookingId = this.onsetBookingId();
    if (bookingId) {
      this.#placementTestsService.sendStudyGuideTestResult(this.#urlService.onsetServiceUrl(), bookingId).subscribe();
    }
  }

  onTestResult(test: any): void {
    console.log(`test result ${test}`);
  }

  #generateClozeTestModel(): ClozeTestModel {
    const onsetServiceUrl = this.#urlService.onsetServiceUrl();
    // const onsetServiceUrl = 'https://apprex.gast.de/gast-service-onset/';
    return {
      onsetServiceUrl,
      startWithBuchungId: this.onsetBookingId() ?? 0,
    };
  }

  #generateClozeTestStyle(): ClozeTestStyleModel {
    const model = {} as ClozeTestStyleModel;
    // model.siteBackground = '#e0715dff';
    model.siteBackground = '#f8f8f6';
    model.exerciseBackground = '#ffffff';
    model.frameColor = '#DEDEE1';
    model.barColor = '#b3c9de';
    model.specialCharacterListColor = '#6852AA';
    model.specialCharacterListDisabledColor = '#F3F1F9';
    model.specialCharacterTextColor = '#ffffff';
    model.exerciseTextColor = '#000000';
    model.buttonsColor = '#FFE700';
    model.logo = 'assets/daad-logo.svg';
    model.font = '';
    model.fontSize = '14pt';
    model.displayLogo = true;
    model.cTestInformationUrl = 'https://www.myguide.de';
    return model;
  }
}
